import { useTranslation } from 'next-i18next'
import { Dispatch, SetStateAction, useMemo } from 'react'

import { useGetActivityReward } from '~/bootcamp/Activities/hooks/useGetActivityReward'
import RewardSection from '~/bootcamp/pages/BootcampView/BootcampContent/RewardSection'
import { setIsRewardFeedbackScreenOpenActionCreator } from '~/bootcamp/slices/bootcamp-activity.slice'
import randomFeedback from '~/bootcamp/utils/randomFeedback'
import under13Pose from '~/bootcamp/utils/under13Pose.utils'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import Spinner from '~/components/Spinner'
import appConfig from '~/config/app'
import useAvatarImageUrl from '~/dashboard/hooks/useAvatarImageUrl'
import { getReadyPlayerMeAvatarUrl, isReadyPlayerMeAvatarUrl } from '~/dashboard/hooks/util'
import { OutfitGender } from '~/homebase/types/Homebase.types'
import useCurrentUser from '~/hooks/useCurrentUser'
import { selectIsPreSignup } from '~/onboarding/redux/onboarding.slice'
import { useGetAvatarMetadataQuery } from '~/redux/apis/user-api'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { IUser } from '~/types/user-info-types'
import { Breakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'

const MODAL_ID = 'rewards-modal'

export default function RewardFeedbackScreen({
  setStartAnimation,
  rewards: rewardsProp,
}: {
  setStartAnimation: Dispatch<SetStateAction<boolean>>
  rewards?: { gems: number; xp: number; kudos: number }
}): React.ReactElement {
  const { t } = useTranslation('avatar')
  const dispatch = useAppDispatch()
  const avatarImageObject = useAvatarImageUrl()
  const isPreSignup = useAppSelector(selectIsPreSignup)

  const user = useCurrentUser()
  const { data: metadata } = useGetAvatarMetadataQuery(user as IUser, { skip: !user?.avatarUrl })
  const isFemale = metadata?.outfitGender === OutfitGender.FEMININE
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })
  const avatarFeedback = useMemo(() => randomFeedback(), [])

  const avatarImage =
    avatarImageObject.isUnder13 || !avatarImageObject.isExists
      ? user?.avatarUrl
        ? under13Pose({ avatarUrl: user?.avatarUrl })
        : avatarImageObject.url
      : isReadyPlayerMeAvatarUrl(user!.avatarUrl!)
      ? getReadyPlayerMeAvatarUrl(user!.avatarUrl!, avatarFeedback.avatarPose)
      : `${avatarImageObject.url}${avatarFeedback.avatarPose}` // this is the legacy avatar url from s3 or googleapis

  const bgSize = isMobile ? 'bg-full' : 'bg-cover'
  const rewardsFromHook = useGetActivityReward()
  const rewards = rewardsProp ?? rewardsFromHook
  const onClick = (event: any): void => {
    if (document.getElementById(MODAL_ID)?.contains(event.target)) return
    setStartAnimation(false)
  }

  const AvatarImageComponent = isPreSignup ? (
    <ResponsiveImage
      alt={t('yourAvatar')}
      imageClassName="text-white w-87.5 md:w-96 -mt-10 translate-y-10 md:translate-y-0"
      imageWidths="410"
      srcKey="avatars/custom/under-13/png/A_Boy_01_profilePhoto_v02.png"
    />
  ) : !avatarImageObject.isExists || avatarImageObject.isUnder13 ? (
    <ResponsiveImage
      alt={t('yourAvatar')}
      fit="contain"
      height={410}
      imageClassName="text-white"
      imageWidths="410"
      srcKey={avatarImage || avatarImageObject.url}
      width={410}
    />
  ) : (
    <ResponsiveImage
      alt={t('yourAvatar')}
      height={410}
      imageClassName="text-white"
      imageWidths="410"
      loading="eager"
      src={avatarImage}
      width={410}
    />
  )

  return (
    <aside
      className="fixed top-0 right-0 bottom-0 left-0 z-modal bg-black/80 flex items-center"
      onClick={onClick}
      title="Reward Feedback Screen modal"
    >
      <button
        className={`relative mx-auto bg-purple-darker text-white w-[93%] max-w-[620px] rounded h-3/4 max-h-[600px] shadow-large ${bgSize}`}
        onClick={() => {
          setStartAnimation(false)
          dispatch(setIsRewardFeedbackScreenOpenActionCreator(false))
        }}
        style={{
          backgroundImage: `url(${appConfig.assetsCdnUrl}/bootcamp/reward-chest-background.svg)`,
        }}
      >
        {!isPreSignup && avatarImageObject.isLoading ? (
          <div className="flex justify-center items-center h-full w-full">
            <Spinner size={50} />
          </div>
        ) : (
          <div className="relative  h-full w-full flex flex-col justify-end items-center">
            <div className="relative z-10">
              <div className={`absolute w-full h-full ${isFemale ? '-mt-4 md:mt-6 -ml-5' : '-mt-8'}`}>
                <div className="absolute left-1/3 ml-6 sm:left-48 -top-5 md:-top-4 triangle scale-50 rotate-30 z-0" />
                <div className="absolute left-1/3 ml-6 sm:left-48 -top-4.5 md:-top-3 triangle scale-20 rotate-30 !bg-purple z-20" />
                <div className="flex items-center text-center absolute left-1/2 ml-4 sm:left-64 bg-white rounded-full h-16 md:h-20 min-w-32 md:min-w-40 rotate-9 z-10">
                  <p
                    className={`bg-purple font-semibold w-full px-2 ml-2 mr-1 rounded-full h-14 md:h-16 flex items-center justify-center text-white z-20 ${avatarFeedback.avatarMessage.size}`}
                  >
                    {t(`feedback.${avatarFeedback.avatarMessage.text}`)}
                  </p>
                </div>
              </div>
              {AvatarImageComponent}
            </div>
            <RewardSection rewards={rewards} />
          </div>
        )}
      </button>
    </aside>
  )
}
